import { useEffect, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';
import { cloneDeep } from 'lodash';
import { useConditionalEffect } from 'react-delta';

import EditIcon from '@mui/icons-material/Edit';
import ShowIcon from '@mui/icons-material/Visibility';
import HideIcon from '@mui/icons-material/VisibilityOff';
import MarkerIcon from '@mui/icons-material/FiberManualRecord';
import PrevIcon from '@mui/icons-material/KeyboardArrowLeft';
import NextIcon from '@mui/icons-material/KeyboardArrowRight';

import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { useIsNewSearch } from 'hooks/useIsNewSearch';

import * as K from 'common/constants';
import Dropdown from 'components/common/Dropdown/Dropdown';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import AddEditPlotsModal from 'components/dashboards/DashboardPage/modals/AddEditPlotsModal';
import { EDIT_DASHBOARD_GRAPH, GET_CONFIGV2 } from 'components/dashboards/queries';
import { getPlots, getType } from 'components/dashboards/dashboardData';
import { DashboardParam, GraphConfigType, GraphPlotConfigType } from 'components/dashboards/types';
import { elliptifyLabelText, getLrqStatus, LRQ_STATUS } from 'common/util/graph';
import { queryTimeRangeState, searchParamState } from 'components/common/Managers/RouteStateManager.state';

import './DashboardChartLegend.scss';

const MAX_LABEL_LEN = 30;

interface DashboardChartLegendType {
  config: GraphConfigType;
  dataIds: string[];
  plotColors: string[];
  dashboardName: string;
  chartIndex: number;
  params: Array<DashboardParam>;
  highChartInstance?: Highcharts.Chart;
}

const DashboardChartLegend = ({
  config,
  dataIds,
  plotColors,
  params,
  dashboardName,
  chartIndex,
  highChartInstance,
}: DashboardChartLegendType): JSX.Element => {
  const [showAddPlots, setShowAddPlots] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState({});
  const [selectedPlotIndex, setSelectedPlotIndex] = useState(-1);
  const vizType = getType(config);

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const tabName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));
  const { startTime, endTime } = queryTimeRange;
  const [saveGraphToDashboard] = useMutation(EDIT_DASHBOARD_GRAPH, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName, startTime, endTime } }],
  });

  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const isNewSearch = useIsNewSearch();
  const { results, errors } = useEventCache(dataIds);
  const lrqStatus = getLrqStatus(results, errors, true);
  const noResults = lrqStatus !== LRQ_STATUS.DATA_READY;
  const plots = noResults ? [] : getPlots(config, results, plotColors, params);
  const [hiddenPlots, setHiddenPlots] = useState(new Array<boolean>(plots.length).fill(false));

  // The wonderful world of custom scrolling...
  const refWrapper = useRef<HTMLDivElement>(null);
  const [scrollLeftPos, setScrollLeftPos] = useState(0);
  const [currentLeftIndex, setCurrentLeftIndex] = useState(0);
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);

  useConditionalEffect(() => {
    setHiddenPlots(new Array<boolean>(plots.length).fill(false));
  }, isNewSearch);

  /**
   *  This function makes sure that the legend wrapper scroll position is in sync with the page. It gets
   *  called 1) initially, 2) when the chart's width changes and 3) when the user clicks next or prev.
   *
   *  @param legendIndexOffset number: -1 to scroll left, 1 to scroll right, 0 to just sync state
   */
  const setLegendPosition = (legendIndexOffset: number): void => {
    if (refWrapper.current) {
      const legendWrapper = refWrapper.current;
      const legendWidth = legendWrapper.offsetWidth;
      const newScrollLeftIndex = Math.max(currentLeftIndex + legendIndexOffset, 0);
      const children = Array.from(refWrapper.current.childNodes, (child) => child as HTMLElement);
      if (children.length) {
        const widths = children.map((child) => child.offsetWidth);

        let newScrollLeftPos = 0;
        for (let i = 0; i < newScrollLeftIndex; i += 1) {
          newScrollLeftPos += widths[i];
        }

        // If scrolling, animate it the old-fashioned way -- using React state is too slow
        if (legendIndexOffset) {
          let left = scrollLeftPos;
          const numSteps = legendWidth / 100;
          const offset = (newScrollLeftPos - scrollLeftPos) / numSteps;
          let step = 0;
          if (legendIndexOffset) {
            const timerId = setInterval(() => {
              left += offset;
              legendWrapper.scrollLeft = left;
              step += 1;
              if (step >= numSteps) {
                clearInterval(timerId);
              }
            }, 20);
          }
        }

        // Catch the state up to where we're at now
        legendWrapper.scrollLeft = newScrollLeftPos;
        setScrollLeftPos(newScrollLeftPos);
        setCurrentLeftIndex(newScrollLeftIndex);

        // The right scroll button is only enabled if the last item can't fit on the display
        const rightItemEdge = children[children.length - 1].offsetLeft + widths[children.length - 1];
        // Chop off the width of the buttons before deciding if things fit
        setNextButtonEnabled(rightItemEdge - 40 >= newScrollLeftPos + legendWidth);
      }
    }
  };

  useEffect(() => {
    if (refWrapper && refWrapper.current) {
      setLegendPosition(0);
    }
  });

  if (noResults || vizType.isReport) {
    return <></>;
  }

  const editPlot = (index: number): void => {
    setSelectedPlotIndex(index);
    if (config.plots) {
      // specifically set original plot from config.plots instead of from normalizedPlots
      setSelectedPlot(config.plots[index]);
    }
    setShowAddPlots(true);
  };

  /**
   * Handle the plot modal submit callback to save a plot.
   */
  const handlePlotModalSubmit = (plot: GraphPlotConfigType): void => {
    const configCopy = cloneDeep(config);
    configCopy.plots = configCopy.plots || [];
    configCopy.plots[selectedPlotIndex] = plot;

    const variables: { [key: string]: string | number } = {
      dashboardName,
      graph: JSON.stringify(configCopy),
      graphIndex: chartIndex,
      tabName,
    };

    saveGraphToDashboard({ variables })
      .then(() => {
        setShowAddPlots(false);
      })
      .catch((error) => {
        setPageAlertConfig({ message: error.message, autoHide: true });
      });
  };

  const togglePlotVisibility = (index: number): void => {
    if (highChartInstance) {
      const newHiddenPlots = [...hiddenPlots];
      const series = highChartInstance.series;
      if (series[index].visible) {
        series[index].hide();
        newHiddenPlots[index] = true;
      } else {
        series[index].show();
        newHiddenPlots[index] = false;
      }
      setHiddenPlots(newHiddenPlots);
    }
  };

  if (config.shift) {
    plots.push({
      label: 'delta',
    });
  }

  const dropdownButtons = plots.map((plot, plotIndex) => {
    const key = (plot.label || 'Untitled') + plotIndex;
    const menuItems = [
      {
        children: (
          <>
            {hiddenPlots[plotIndex] ? <ShowIcon fontSize="small" /> : <HideIcon fontSize="small" />}
            {hiddenPlots[plotIndex] ? '  Show Plot' : '  Hide Plot'}
          </>
        ),
        clickHandler: (): void => {
          togglePlotVisibility(plotIndex);
        },
      },
    ];

    if (!config.breakdownFacet && !vizType.isPowerQuery) {
      menuItems.push({
        children: (
          <>
            <EditIcon fontSize="small" />
            {'  Edit Plot'}
          </>
        ),
        clickHandler: (): void => {
          editPlot(plotIndex);
        },
      });
    }

    return (
      <Dropdown
        className="DashboardChartLegend__Button"
        variant={ButtonVariant.BORDERLESS}
        key={key}
        sections={[menuItems]}
        size="sm"
      >
        {hiddenPlots[plotIndex] ? (
          <MarkerIcon className="DashboardChartLegend__Marker DashboardChartLegend__Marker--Hidden" />
        ) : (
          <MarkerIcon className="DashboardChartLegend__Marker" htmlColor={plot.color} />
        )}
        {elliptifyLabelText(plot.label || 'Untitled', MAX_LABEL_LEN, true)}
      </Dropdown>
    );
  });

  const prevButtonEnabled = currentLeftIndex > 0;
  const btnClassName = prevButtonEnabled || nextButtonEnabled ? '' : ' DashboardChartLegend__Btn--Hidden';
  const legendClassName = prevButtonEnabled || nextButtonEnabled ? '' : ' DashboardChartLegend--No-Buttons';

  return (
    <>
      <div className={`DashboardChartLegend${legendClassName}`}>
        <Button
          className={`DashboardChartLegend__Prev${btnClassName}`}
          disabled={!prevButtonEnabled}
          onClick={(): void => setLegendPosition(-1)}
        >
          <PrevIcon fontSize="small" />
        </Button>
        <div className="DashboardChartLegend__Wrapper" ref={refWrapper}>
          {dropdownButtons}
        </div>
        <Button
          className={`DashboardChartLegend__Next${btnClassName}`}
          disabled={!nextButtonEnabled}
          onClick={(): void => setLegendPosition(1)}
        >
          <NextIcon fontSize="small" />
        </Button>
      </div>
      {showAddPlots ? (
        <AddEditPlotsModal
          showModal={showAddPlots}
          plot={selectedPlot}
          isEdit
          plotIndex={selectedPlotIndex}
          plotModalSubmitCallback={handlePlotModalSubmit}
          handleClose={(): void => {
            setShowAddPlots(false);
          }}
        />
      ) : null}
    </>
  );
};

export default DashboardChartLegend;
